import React, { useState, useMemo, useEffect } from 'react';
import { Icon } from './icons';
import { money } from './format';
import { Modal } from './sheets';
import { toKey, buildBudgetRows } from './dataAdapters';
import { suggestEnvelopeLimits } from '../lib/forecasting';

function SuggestLimitsModal({ open, onClose, transactions, categories, envelopes, onApply, lookbackMonths = 3 }) {
  const suggestions = useMemo(() => {
    if (!open) return {};
    return suggestEnvelopeLimits(transactions, categories, { lookbackMonths });
  }, [open, transactions, categories, lookbackMonths]);

  const rows = useMemo(() => {
    return categories
      .map((cat) => {
        const s = suggestions[cat];
        if (!s) return null;
        const current = envelopes[toKey(cat)];
        const isChange = current === undefined || Math.abs((current || 0) - s.suggested) >= 1;
        return { cat, current, ...s, isChange };
      })
      .filter(Boolean)
      .sort((a, b) => b.suggested - a.suggested);
  }, [suggestions, categories, envelopes]);

  const [selected, setSelected] = useState({});
  useEffect(() => {
    if (!open) return;
    const init = {};
    rows.forEach((r) => { init[r.cat] = r.isChange; });
    setSelected(init);
  }, [open, rows]);

  const applyChanges = () => {
    const changes = {};
    rows.forEach((r) => {
      if (selected[r.cat]) changes[r.cat] = r.suggested;
    });
    onApply(changes);
    onClose();
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <Modal open={open} onClose={onClose} title="Suggested limits" width={520}>
      <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 16 }}>
        Based on the last {lookbackMonths} {lookbackMonths === 1 ? 'month' : 'months'} of spending.
      </p>
      {rows.length === 0 ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: 14 }}>
          Not enough spending history to suggest limits yet.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginBottom: 16 }}>
            {rows.map((r) => (
              <label
                key={r.cat}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 4px', borderTop: '1px solid var(--line)', cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={!!selected[r.cat]}
                  onChange={(e) => setSelected((s) => ({ ...s, [r.cat]: e.target.checked }))}
                  style={{ width: 16, height: 16, accentColor: 'var(--ink)' }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="title" style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{r.cat}</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                    Avg ${r.average.toFixed(0)} · p90 ${r.p90.toFixed(0)} · {r.sampleSize}/{r.months} months
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="num" style={{ fontSize: 15, fontWeight: 600, color: r.isChange ? 'var(--accent)' : 'var(--ink-3)' }}>
                    ${r.suggested.toFixed(0)}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    {r.current === undefined ? 'no limit set' : `was $${(r.current || 0).toFixed(0)}`}
                  </div>
                </div>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} className="btn-quiet is-block pressable">
              Cancel
            </button>
            <button
              onClick={applyChanges}
              disabled={selectedCount === 0}
              className="btn-primary is-block pressable"
              style={{ flex: 2 }}
            >
              Apply {selectedCount} {selectedCount === 1 ? 'limit' : 'limits'}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

export function BudgetView({
  transactions,
  priorMonthTransactions = [],
  allTransactions = [],
  envelopes,
  envelopeMeta = {},
  categories,
  onSave,
  onDelete,
  onToggleRollover,
  onBulkSetLimits,
  desktop,
  monthLabel = 'This month',
  onViewTransactions,
}) {
  const [editingCat, setEditingCat] = useState(null);
  const [limitInput, setLimitInput] = useState('');
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [savedCat, setSavedCat] = useState(null); // category key that just saved

  const handleSave = (cat, val) => {
    try {
      onSave(cat, val);
      setSavedCat(cat);
      setTimeout(() => setSavedCat(null), 1600);
    } catch (err) {
      console.error('Envelope save error:', err);
    }
    setEditingCat(null);
  };

  // Shared with the dashboard's budget card via buildBudgetRows so both use the
  // same spend/rollover math and the same (transfer-excluded) transaction lens.
  const rows = buildBudgetRows(envelopes, categories, transactions, {
    envelopeMeta,
    priorMonthTransactions,
  });
  const budgetRows = rows.filter((r) => r.hasLimit);
  const unbudgeted = rows
    .filter((r) => !r.hasLimit)
    .map((r) => ({ cat: r.cat, spent: r.spent }))
    .sort((a, b) => b.spent - a.spent);

  const totalBudgeted = budgetRows.reduce((s, r) => s + r.limit, 0);
  const totalSpent = budgetRows.reduce((s, r) => s + r.spent, 0);

  const totalRemaining = totalBudgeted - totalSpent;
  const overCount = budgetRows.filter((r) => r.over).length;
  const daysLeft = (() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate() - now.getDate();
  })();

  return (
    <div className="pg">

      {/* One lead figure with a sentence, not three equal stat cards — the
          remaining dollars are the answer; budgeted and spent are its working. */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18, flexWrap: 'wrap' }}>
        <div className="pg-figure" style={{ color: totalRemaining < 0 ? 'var(--alarm)' : 'var(--ink)' }}>
          {money(totalRemaining, { cents: false })}
        </div>
        <div className="pg-lede">
          left of the <b style={{ color: 'var(--ink)', fontWeight: 600 }}>{money(totalBudgeted, { cents: false })}</b> you
          budgeted{monthLabel === 'This month' ? <> this month, with <b style={{ color: 'var(--ink)', fontWeight: 600 }}>{daysLeft} days</b> to go</> : <> in {monthLabel}</>}
        </div>
        <div style={{ flexGrow: 1 }} />
        {onBulkSetLimits && (
          <button type="button" onClick={() => setSuggestOpen(true)} className="btn-primary pressable" style={{ marginBottom: 6 }}>
            <Icon name="bolt" size={15} />
            Suggest limits from history
          </button>
        )}
      </div>

      {budgetRows.length > 0 && (
        <div className="pg-section">
          <div className="pg-head">
            <div className="eyebrow">Budgeted categories</div>
            <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
              {overCount === 0
                ? 'nothing over — nothing here needs you'
                : `${overCount === 1 ? 'one category' : `${overCount} categories`} over · nothing else needs you`}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {budgetRows.map((row) => {
              const effective = row.effectiveLimit ?? row.limit;
              const remaining = effective - row.spent;
              return (
                <div key={row.cat} className="row-line" style={{ flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={onViewTransactions ? () => onViewTransactions(row.cat) : undefined}
                    aria-label={`${row.cat}: view transactions`}
                    style={{ width: 190, flexShrink: 0, textAlign: 'left', background: 'transparent', padding: 0, fontSize: 14.5, fontWeight: 500, color: 'var(--ink)' }}
                  >
                    {row.cat}
                    {row.rolloverEnabled && row.rolloverBonus > 0 && (
                      <span className="num" style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: 'var(--ink-3)' }}>
                        +{money(row.rolloverBonus, { cents: false })} rolled over
                      </span>
                    )}
                  </button>
                  <span className="num" style={{ width: 150, flexShrink: 0, fontSize: 13.5, color: 'var(--ink-2)' }}>
                    {money(row.spent, { cents: false })} <span style={{ color: 'var(--ink-3)' }}>/ {money(effective, { cents: false })}</span>
                  </span>

                  <div className="row-actions">
                    {onToggleRollover && (
                      <button type="button" onClick={() => onToggleRollover(row.cat, !row.rolloverEnabled)} className={`chip pressable ${row.rolloverEnabled ? 'is-active' : ''}`} style={{ height: 26, fontSize: 11.5 }}>
                        {row.rolloverEnabled ? 'Rollover on' : 'Rollover off'}
                      </button>
                    )}
                    <button type="button" onClick={() => { setEditingCat(row.cat); setLimitInput(row.limit); }} className="chip pressable" style={{ height: 26, fontSize: 11.5 }}>
                      Edit limit
                    </button>
                    <button type="button" onClick={() => onDelete(row.cat)} aria-label={`Delete the ${row.cat} budget`} className="chip pressable" style={{ height: 26, width: 26, padding: 0, justifyContent: 'center' }}>
                      <Icon name="trash" size={13} />
                    </button>
                  </div>

                  <div style={{ flexGrow: 1 }} />
                  <span className="num" style={{ fontSize: 14.5, fontWeight: 600, color: row.over ? 'var(--alarm)' : 'var(--ink)' }}>
                    {row.over
                      ? <>{'\u2212'}{money(Math.abs(remaining), { cents: false })} over</>
                      : <>{money(remaining, { cents: false })} left</>}
                  </span>

                  {editingCat === row.cat && (
                    <div style={{ display: 'flex', gap: 8, width: '100%', marginTop: 10 }}>
                      <input
                        autoFocus
                        type="number"
                        step="0.01"
                        value={limitInput}
                        onChange={(e) => setLimitInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSave(row.cat, limitInput);
                          if (e.key === 'Escape') setEditingCat(null);
                        }}
                        className={savedCat === row.cat ? 'field input-saved' : 'field'}
                        style={{ flex: 1 }}
                      />
                      <button onClick={() => handleSave(row.cat, limitInput)} className="btn-primary pressable">Save</button>
                      <button onClick={() => setEditingCat(null)} className="btn-quiet pressable">Cancel</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {unbudgeted.length > 0 && (
        <div className="pg-section">
          <div className="eyebrow">Not budgeted</div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {unbudgeted.map(({ cat, spent }) => (
              <div key={cat} className="row-line">
                <span style={{ flexGrow: 1, fontSize: 14, fontWeight: 500, color: 'var(--ink-2)' }}>{cat}</span>
                <span className="num" style={{ fontSize: 13, color: 'var(--ink-3)' }}>{money(spent, { cents: false })} spent</span>
                <button onClick={() => onSave(cat, 0)} className="pressable" style={{ marginLeft: 18, background: 'transparent', padding: 0, fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>
                  Set limit
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {budgetRows.length === 0 && (
        <p className="pg-note">No budgets set yet — pick a category under &ldquo;Not budgeted&rdquo; to give it a monthly limit.</p>
      )}

      <SuggestLimitsModal
        open={suggestOpen}
        onClose={() => setSuggestOpen(false)}
        transactions={allTransactions}
        categories={categories}
        envelopes={envelopes}
        onApply={onBulkSetLimits || (() => {})}
      />
    </div>
  );
}
